<script setup lang="ts">
  import { computed } from 'vue'
  import {
    ComboboxHeader,
    ComboboxHeaderButton,
    ComboboxHeaderTitle,
    FlagIcon,
    InputGroupAddon,
    InputGroupCombobox
  } from '@gat-ship/vue-components'
  import { IconCheck } from '@gat-ship/icons'
  import { useSelectCountryDataSource } from '../../composables/useSelectCountryDataSource'
  import { toSelectItems } from './to-select-items'
  import type { RefDataPickerEmits, RefDataPickerProps } from './picker-props'

  /**
   * A country picker: owns its own data fetching and composes the generic
   * `InputGroupCombobox` primitive with country chrome — the selected
   * country's flag renders as a leading `InputGroupAddon` (not inside the
   * combobox), and dropdown rows show a flag + name + code.
   *
   * It brings no border of its own — drop it into a `FormField` default slot
   * (like `InputGroupInput`) and it picks up the parent `InputGroup`'s focus
   * / error chrome. Can also be used standalone with `v-model`.
   *
   * Usage:
   * ```vue
   * <FormField :form="form" name="countryCode" label="Country" required>
   *   <template #default="{ field, invalid }">
   *     <CountryPicker
   *       :id="field.name"
   *       :model-value="field.state.value"
   *       :aria-invalid="invalid"
   *       @update:model-value="field.handleChange"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  const props = defineProps<
    RefDataPickerProps & {
      /** Pre-add a country so it shows even before the full list loads. */
      preSelectedCountryCode?: string
    }
  >()

  defineEmits<RefDataPickerEmits>()

  const { countries, loadingCountries, fetchCountries, getCountry } =
    useSelectCountryDataSource()

  // Lazy fetch on first render
  fetchCountries()

  // Pre-add a country if provided
  if (props.preSelectedCountryCode) {
    getCountry(props.preSelectedCountryCode)
  }

  const countryItems = computed(() =>
    toSelectItems(countries.value, {
      value: (c) => c.code,
      title: (c) => c.name,
      sort: (a, b) => a.name.localeCompare(b.name)
    })
  )
</script>

<template>
  <InputGroupCombobox
    :id="id"
    :items="countryItems"
    :model-value="modelValue"
    :loading="loadingCountries"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="ariaInvalid"
    :data-testid="dataTestid"
    :input-class="inputClass"
    @update:model-value="$emit('update:modelValue', $event)"
    @blur="$emit('blur')"
  >
    <template #content-header>
      <ComboboxHeader>
        <ComboboxHeaderTitle>Countries</ComboboxHeaderTitle>
        <ComboboxHeaderButton
          v-if="canManage"
          tabindex="-1"
          @click="$emit('manage')"
        >
          Manage
        </ComboboxHeaderButton>
      </ComboboxHeader>
    </template>
    <template #leading="{ item }">
      <InputGroupAddon v-if="item" align="inline-start" class="shrink-0">
        <FlagIcon :flag="item.value" class="size-4 shrink-0 rounded-full" />
      </InputGroupAddon>
    </template>
    <template #item="{ item, selected }">
      <FlagIcon
        :flag="item.value"
        variant="rectangle"
        class="bg-foreground/20 flex h-4 w-6 shrink-0 overflow-hidden rounded-sm"
      />
      <span class="flex-1 truncate text-sm">{{ item.title }}</span>
      <span class="text-muted-foreground text-xs tabular-nums">
        {{ item.value }}
      </span>
      <IconCheck
        :class="[
          'ml-auto size-3.5 shrink-0',
          selected ? 'opacity-100' : 'opacity-0'
        ]"
      />
    </template>
  </InputGroupCombobox>
</template>
